import {
  Participant,
  type ParticipantReferenceString,
} from '@seen/core/models/patients/participant'
import type { ReactNode } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { ParticipantLabelBadge } from '#src/components/ParticipantLabelBadge.tsx'
import { ParticipantLabelCombobox } from '#src/components/ParticipantLabelCombobox.tsx'
import {
  FacesheetCard,
  FacesheetCardActions,
  FacesheetCardAddButton,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardCount,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function ParticipantLabelsCard({
  participantReferenceString,
}: {
  participantReferenceString: ParticipantReferenceString
}): ReactNode {
  const isReadOnly = useIsReadOnly()
  const { data: participant } = useRead(Participant, participantReferenceString)
  if (!participant) {
    return null
  }
  const hasLabels = participant.labels.length > 0
  return (
    <FacesheetCard collapsible={hasLabels}>
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            <IconLabel />
          </FacesheetCardIcon>
          <FacesheetCardLabel>Labels</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
        <FacesheetCardActions>
          {!!participant.labels.length && (
            <FacesheetCardCount>{participant.labels.length}</FacesheetCardCount>
          )}
          {!isReadOnly && (
            <ResourceFieldForm
              className="flex"
              instance={participant}
              valueFn={(t) => t.labels}
              patchFn={(t, value) => {
                t.labels = value
              }}
              render={({ field: { value, onChange, ...rest } }) => (
                <ParticipantLabelCombobox value={value} onChange={onChange}>
                  <FacesheetCardAddButton aria-label="Add label" {...rest} />
                </ParticipantLabelCombobox>
              )}
            />
          )}
        </FacesheetCardActions>
      </FacesheetCardHeader>
      {hasLabels && (
        <FacesheetCardContent>
          {isReadOnly && (
            <div className="flex flex-wrap gap-1">
              {participant.labels.map((labelReferenceString) => (
                <ParticipantLabelBadge
                  key={labelReferenceString}
                  label={labelReferenceString}
                  className="shrink-0"
                />
              ))}
            </div>
          )}
          {!isReadOnly && (
            <ResourceFieldForm
              instance={participant}
              valueFn={(t) => t.labels}
              patchFn={(t, value) => {
                t.labels = value
              }}
              render={({ field: { value, onChange, ...rest } }) => (
                <ParticipantLabelCombobox value={value} onChange={onChange}>
                  <div className="flex flex-wrap gap-1" {...rest}>
                    {participant.labels.map((labelReferenceString) => (
                      <ParticipantLabelBadge
                        key={labelReferenceString}
                        label={labelReferenceString}
                        className="shrink-0"
                      />
                    ))}
                  </div>
                </ParticipantLabelCombobox>
              )}
            />
          )}
        </FacesheetCardContent>
      )}
    </FacesheetCard>
  )
}
